import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { useLocation } from "react-router-dom";

type CampaignItem = {
  id: number;
  title: string;
  slug?: string;
  short_description?: string;
  image_url?: string;
  raised_amount?: number | string;
  goal_amount?: number | string;
};

const siteUrl = "https://www.sevaafoundation.com";
const pageUrl = `${siteUrl}/campaign`;
const backendUrl = "https://www.sevaafoundation.com/seva-backend";

/*
  SEO Important:
  1. Upload this image in public folder as: /og-campaigns.jpg
  2. Recommended size: 1200x630px
  3. Do not use logo512.png as OG image.
*/
const defaultImage = `${siteUrl}/og-blogs.jpg`;

const getFirstImage = (imageString?: string) => {
  if (!imageString) {
    return "https://via.placeholder.com/600x400?text=No+Image";
  }

  const firstImage = imageString.split(",")[0].trim();

  if (firstImage.startsWith("http://") || firstImage.startsWith("https://")) {
    return firstImage;
  }

  return `${backendUrl}${firstImage}`;
};

const Campaigns = () => {
  const location = useLocation();
  const isCampaignRoute = location.pathname === "/campaign";

  const [campaigns, setCampaigns] = useState<CampaignItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // 1. Backend se Campaigns Fetch karna
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/campaigns/list.php`);
        const data = await response.json();

        if (data.success) {
          // Hum top 3 campaigns dikha rahe hain jaise original design me the
          setCampaigns(data.campaigns.slice(0, 3));
        }
      } catch (error) {
        console.error("Failed to fetch campaigns", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCampaigns();
  }, []);

  // 2. Donate button logic - URL mein ID pass karna
  const openDonate = (id: number) => {
    window.dispatchEvent(
      new CustomEvent("openDonationModal", { detail: { campaignId: id } })
    );
  };

  // SEO Structured Data - sirf /campaign route ke liye use hoga.
  // Home page par ye Helmet render nahi hoga, isliye Home ka canonical/title disturb nahi hoga.
  const campaignPageSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "@id": `${pageUrl}#collectionpage`,
      name: "NGO Campaigns in India | Sevaa Foundation",
      alternateName: "Sevaa Foundation NGO Initiatives",
      url: pageUrl,
      inLanguage: "en-IN",
      description:
        "Explore active NGO campaigns by Sevaa Foundation for child education, healthcare support, hunger relief, women empowerment and social welfare in India.",
      isPartOf: {
        "@type": "WebSite",
        "@id": `${siteUrl}/#website`,
        name: "Sevaa Foundation",
        url: siteUrl,
      },
      publisher: {
        "@type": "NGO",
        "@id": `${siteUrl}/#ngo`,
        name: "Sevaa Foundation",
        alternateName: "Swadesh Sevaa Charitable Foundation",
        url: siteUrl,
        logo: {
          "@type": "ImageObject",
          url: `${siteUrl}/logo512.png`,
        },
        image: defaultImage,
        areaServed: {
          "@type": "Country",
          name: "India",
        },
      },
      about: [
        "NGO Campaigns",
        "Child Education Campaign",
        "Healthcare Support Campaign",
        "Hunger Relief Campaign",
        "Women Empowerment Campaign",
        "Donation Campaign India",
        "Social Welfare Initiatives",
      ],
      mainEntity: {
        "@type": "ItemList",
        itemListOrder: "https://schema.org/ItemListOrderAscending",
        numberOfItems: campaigns.length,
        itemListElement: campaigns.map((campaign, index) => ({
          "@type": "ListItem",
          position: index + 1,
          item: {
            "@type": "DonateAction",
            name: campaign.title,
            description: campaign.short_description,
            image: campaign.image_url
              ? getFirstImage(campaign.image_url)
              : defaultImage,
            url: pageUrl,
            recipient: {
              "@type": "NGO",
              name: "Sevaa Foundation",
            },
          },
        })),
      },
    }),
    [campaigns]
  );

  const breadcrumbSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${pageUrl}#breadcrumb`,
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: `${siteUrl}/`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Campaigns",
          item: pageUrl,
        },
      ],
    }),
    []
  );

  const HeadingTag = isCampaignRoute ? "h1" : "h2";

  return (
    <>
      {isCampaignRoute && (
        <Helmet>
          <title>NGO Campaigns in India | Sevaa Foundation</title>

          <meta
            name="title"
            content="NGO Campaigns in India | Sevaa Foundation"
          />

          <meta
            name="description"
            content="Explore Sevaa Foundation NGO campaigns for child education, healthcare, hunger relief and women empowerment. Donate and support verified initiatives."
          />

          <meta
            name="keywords"
            content="Sevaa Foundation campaigns, NGO campaigns India, donation campaign India, child education campaign, healthcare support campaign, hunger relief campaign, women empowerment campaign, charity campaign, social welfare campaign, गरीब बच्चों की मदद, NGO donation, samaj seva, charity India, Sevaa Foundation initiative, Swadesh Sevaa Charitable Foundation"
          />

          <meta name="robots" content="index, follow, max-image-preview:large" />
          <meta name="author" content="Sevaa Foundation" />
          <meta name="theme-color" content="#0f766e" />
          <meta name="language" content="English, Hindi" />

          <link rel="canonical" href={pageUrl} />

          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="Sevaa Foundation" />
          <meta property="og:url" content={pageUrl} />
          <meta
            property="og:title"
            content="NGO Campaigns in India | Sevaa Foundation"
          />
          <meta
            property="og:description"
            content="Support Sevaa Foundation campaigns for education, healthcare, hunger relief, women empowerment and social welfare."
          />
          <meta property="og:image" content={defaultImage} />
          <meta
            property="og:image:alt"
            content="Sevaa Foundation NGO campaigns for education, healthcare and hunger relief"
          />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:locale" content="en_IN" />

          <meta name="twitter:card" content="summary_large_image" />
          <meta
            name="twitter:title"
            content="NGO Campaigns in India | Sevaa Foundation"
          />
          <meta
            name="twitter:description"
            content="Explore verified NGO campaigns by Sevaa Foundation and contribute to education, healthcare, hunger support and women empowerment."
          />
          <meta name="twitter:image" content={defaultImage} />
          <meta
            name="twitter:image:alt"
            content="Sevaa Foundation NGO campaigns for social welfare in India"
          />

          <script type="application/ld+json">
            {JSON.stringify(campaignPageSchema)}
          </script>

          <script type="application/ld+json">
            {JSON.stringify(breadcrumbSchema)}
          </script>
        </Helmet>
      )}

      <div className="py-16 bg-[#fcfcfc] font-inter">
        <div className="max-w-8xl mx-auto px-6 lg:px-8">
          {/* Official Section Header */}
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 border-b border-slate-100 pb-8">
            <div className="max-w-xl">
              <HeadingTag className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight uppercase">
                Current <span className="text-[#0f766e]">Initiatives</span>
              </HeadingTag>

              <p className="text-slate-500 mt-2 font-medium text-sm">
                Transparency-driven social services by Sevaa Foundation.
              </p>
            </div>

            <div className="mt-4 md:mt-0">
              <div className="bg-white px-4 py-2 rounded-lg border border-slate-200 shadow-sm flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>

                <span className="text-[10px] font-black text-slate-700 uppercase tracking-widest">
                  Active & Verified
                </span>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="animate-spin text-[#0f766e]" size={40} />
            </div>
          ) : (
            /* Clean Service-Style Grid */
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {campaigns.map((c, i) => {
                // Real calculation from backend data
                const raised = Number(c.raised_amount || 0);
                const goal = Number(c.goal_amount || 1);
                const percentage = Math.min(
                  Math.round((raised / goal) * 100),
                  100
                );

                // Ref ID generate karte hain ID se
                const refId = c.slug
                  ? c.slug.substring(0, 3).toUpperCase() + `-${c.id}`
                  : `REF-${c.id}`;

                return (
                  <motion.div
                    key={c.id}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                    className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col"
                  >
                    {/* Image Section */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={getFirstImage(c.image_url)}
                        className="w-full h-full object-cover"
                        alt={`${c.title} - Sevaa Foundation NGO campaign for social welfare in India`}
                        loading="lazy"
                      />

                      <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-md text-white text-[9px] font-black px-2.5 py-1 rounded uppercase tracking-widest">
                        Ref ID: {refId}
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="p-6 flex flex-col flex-grow">
                      <h2
                        className="text-lg font-extrabold text-slate-900 mb-2 tracking-tight leading-tight line-clamp-1"
                        title={c.title}
                      >
                        {c.title}
                      </h2>

                      <p
                        className="text-slate-500 text-xs font-medium mb-6 leading-relaxed line-clamp-2"
                        title={c.short_description}
                      >
                        {c.short_description}
                      </p>

                      <div className="mt-auto">
                        {/* Progress Bar Label */}
                        <div className="flex justify-between items-end mb-3">
                          <div className="flex flex-col">
                            <span className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">
                              Raised
                            </span>

                            <span className="text-base font-black text-[#0f766e]">
                              ₹{raised.toLocaleString("en-IN")}
                            </span>
                          </div>

                          <div className="text-right">
                            <span className="text-[11px] font-black text-orange-600 bg-orange-50 px-2 py-0.5 rounded-md border border-orange-100">
                              {percentage}%
                            </span>
                          </div>
                        </div>

                        {/* Simple Official Progress Bar */}
                        <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden mb-6">
                          <motion.div
                            initial={{ width: 0 }}
                            whileInView={{ width: `${percentage}%` }}
                            viewport={{ once: true }}
                            transition={{ duration: 1.2, ease: "easeOut" }}
                            className="bg-gradient-to-r from-orange-500 to-orange-600 h-full rounded-full"
                          ></motion.div>
                        </div>

                        {/* Action Button */}
                        <button
                          onClick={() => openDonate(c.id)}
                          className="w-full bg-[#0f766e] hover:bg-[#0d5e58] text-white text-[11px] font-black py-4 rounded-xl transition-all uppercase tracking-[0.2em] shadow-lg shadow-teal-900/5 active:scale-95"
                          aria-label={`Contribute to ${c.title} campaign`}
                        >
                          Contribute
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* 80G Tax Exemption Note */}
          <div className="mt-12 text-center">
            <p className="inline-flex items-center gap-2 bg-slate-50 border border-slate-100 px-5 py-2 rounded-full text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              <span className="text-orange-500">★</span> All donations qualify
              for 80G Tax Exemption benefits
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Campaigns; 